import * as React from 'react';
import { compose } from 'redux';

// Component
import SignupInputAuthKey from '../../../SignupInputAuthKey';
import SignupByOTPForm from '../../../SignupByOTPForm';
import Button from '../../../Button';
import SignupTermsOfService from '../../../SignupTermsOfService';
import Logo from '../../../Logo';
import { LoginLink } from '@shopee-user/shared-customization-ui';

// Utils and Hooks
import { Feature } from '@shopee-user/feature-toggle';
import {
  isFeatureEnabled,
  constants,
} from '@shopee-user/spx-customization-logic';
import { useResponsiveLayout, Layout } from '@shopee-user/layout-responsive';
import { useSignupRequireAuthKeyContext } from '@shopee-user/signup-by-otp/src/containers/RequireAuthKeyV2/hook';
import { useTranslation, isWebBridgeSupported } from '@shopee-user/platform';
import {
  withUserWebView,
  WithUserWebViewProps,
} from '@shopee-user/with-web-view';

import commonStyles from '../../../common.styles.scss';
import styles from './style.scss';
import SignupByOtpTip from '../../../SignupByOtpTip';

type Props = WithUserWebViewProps;

function RequirePhoneContent({ webViewNavigateToUrl }: Props) {
  const isSpxOrangeBackgroundUiEnabled = isFeatureEnabled(
    Feature.SPXOrangeBackgroundUI,
    false
  );
  const requireAcceptTermsOfService = isFeatureEnabled(
    Feature.SignupByOtpRequireAcceptTermsAndService,
    false
  );
  const [isAgreementChecked, setIsAgreementChecked] = React.useState(false);

  const { layout } = useResponsiveLayout();

  React.useLayoutEffect(() => {
    if (isSpxOrangeBackgroundUiEnabled && layout === Layout.Mobile) {
      document.body?.classList.add(`${commonStyles.bodyBackground}`);
    }

    return () => {
      if (isSpxOrangeBackgroundUiEnabled && layout === Layout.Mobile) {
        document.body?.classList.remove(`${commonStyles.bodyBackground}`);
      }
    };
  }, [layout]);

  const {
    state: { authKey },
    onSubmitForm,
    shouldDisableSubmit,
    isSubmitButtonDisabled,
  } = useSignupRequireAuthKeyContext();

  const I18n = useTranslation();

  const MobileLogo = layout === Layout.Mobile && (
    <div className={styles.logoContainer}>
      <Logo className={styles.logo} />
    </div>
  );

  const MobileSignupTip = !isFeatureEnabled(Feature.SignupByOtpHideSignupTip) &&
    layout === Layout.Mobile && <SignupByOtpTip />;

  return (
    <div>
      {MobileLogo}
      <div className={styles.formContainer}>
        {MobileSignupTip}
        <SignupByOTPForm
          formInitial={{ authKey }}
          onSubmit={onSubmitForm}
          submitOnEnterPress={!shouldDisableSubmit}
        >
          {({ form }) => {
            return (
              <div>
                <SignupInputAuthKey
                  className={styles.inputAuthKey}
                  {...form.fields.authKey}
                  errors={form.fields.authKey.errors.map(key => I18n.t(key))}
                />
                <Button
                  className={styles.submitButton}
                  text={I18n.t('label_auth_next')}
                  disabled={
                    isSubmitButtonDisabled ||
                    (requireAcceptTermsOfService && !isAgreementChecked) ||
                    !form.fields.authKey.value ||
                    form.fields.authKey.errors.length > 0 ||
                    shouldDisableSubmit
                  }
                  onClick={form.onSubmit}
                />
              </div>
            );
          }}
        </SignupByOTPForm>
        <SignupTermsOfService
          onTermsOfServiceClick={event => {
            event.stopPropagation();
            const termsOfServiceLink = isWebBridgeSupported()
              ? constants.TERMS_OF_SERVICE_WEBVIEW_LINK
              : constants.TERMS_OF_SERVICE_LINK;
            webViewNavigateToUrl(termsOfServiceLink);
          }}
          onPrivacyPolicyClick={event => {
            event.stopPropagation();
            const privacyPolicyLink = isWebBridgeSupported()
              ? constants.PRIVACY_POLICY_WEBVIEW_LINK
              : constants.PRIVACY_POLICY_LINK;
            webViewNavigateToUrl(privacyPolicyLink);
          }}
          onCookiePolicyClick={
            isFeatureEnabled(Feature.LoginTermsOfService)
              ? () => webViewNavigateToUrl(constants.COOKIE_POLICY_LINK)
              : undefined
          }
          checked={isAgreementChecked}
          onCheckboxClick={(checked: boolean) => {
            setIsAgreementChecked(checked);
          }}
        />
        <LoginLink />
      </div>
    </div>
  );
}

export default compose(withUserWebView({}))(RequirePhoneContent);
